import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import api, { calendarAPI, notificationAPI } from '../lib/api';
import {
  Shield, Lock, Eye, Bell, Download, Trash2, Loader2, Check,
  ChevronRight, Smartphone, Key, AlertTriangle, User, Globe, Mail,
  Calendar, Unlink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';

const TABS = [
  { id: 'security', label: 'Security', icon: Shield, desc: 'Password, 2FA, sessions' },
  { id: 'privacy', label: 'Privacy', icon: Eye, desc: 'Visibility, data sharing' },
  { id: 'notifications', label: 'Notifications', icon: Bell, desc: 'Email, push, reminders' },
  { id: 'calendar', label: 'Integrations', icon: Calendar, desc: 'Calendar, third-party' },
  { id: 'data', label: 'Data & Export', icon: Download, desc: 'Download, portability' },
  { id: 'danger', label: 'Danger Zone', icon: AlertTriangle, desc: 'Delete account' },
];

function SecurityTab() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [changingPw, setChangingPw] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });
  const [toggling2FA, setToggling2FA] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);

  useEffect(() => {
    api.get('/settings/security').then(r => { setSettings(r.data); setLoading(false); }).catch(() => {
      setSettings({ two_factor_enabled: false, password_updated_at: null, privacy: {}, notifications: {} });
      setLoading(false);
    });
  }, []);

  const handleChangePassword = async () => {
    if (pwForm.newPw !== pwForm.confirm) { toast.error('Passwords do not match'); return; }
    if (pwForm.newPw.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setChangingPw(true);
    try {
      await api.post('/settings/change-password', { current_password: pwForm.current, new_password: pwForm.newPw });
      toast.success('Password updated successfully');
      setPwForm({ current: '', newPw: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally { setChangingPw(false); }
  };

  const toggle2FA = async () => {
    setToggling2FA(true);
    try {
      const newState = !settings?.two_factor_enabled;
      const res = await api.post('/settings/two-factor', { enabled: newState });
      setSettings(prev => ({ ...prev, two_factor_enabled: newState }));
      if (newState && res.data.backup_codes) setBackupCodes(res.data.backup_codes);
      else setBackupCodes(null);
      toast.success(res.data.message);
    } catch (err) {
      toast.error('Failed to update 2FA');
    } finally { setToggling2FA(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" style={{ color: '#0D9488' }} /></div>;

  return (
    <div className="space-y-6" data-testid="security-settings">
      {/* Password Change */}
      <div className="nx-card p-5">
        <div className="flex items-center gap-2 mb-4">
          <Lock className="w-4 h-4" style={{ color: '#0D9488' }} />
          <h3 className="text-sm font-bold" style={{ color: 'var(--t-text-heading)' }}>Change Password</h3>
        </div>
        <div className="space-y-3 max-w-md">
          <Input type="password" placeholder="Current password" value={pwForm.current}
            onChange={e => setPwForm(p => ({ ...p, current: e.target.value }))}
            className="h-10" data-testid="current-password-input" />
          <Input type="password" placeholder="New password" value={pwForm.newPw}
            onChange={e => setPwForm(p => ({ ...p, newPw: e.target.value }))}
            className="h-10" data-testid="new-password-input" />
          <Input type="password" placeholder="Confirm new password" value={pwForm.confirm}
            onChange={e => setPwForm(p => ({ ...p, confirm: e.target.value }))}
            className="h-10" data-testid="confirm-password-input" />
          <Button onClick={handleChangePassword} disabled={changingPw || !pwForm.current || !pwForm.newPw}
            className="h-9 px-5 text-xs font-semibold rounded-lg" style={{ background: '#1B3461', color: '#fff' }}
            data-testid="change-password-btn">
            {changingPw ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Key className="w-3.5 h-3.5 mr-1.5" />}
            Update Password
          </Button>
        </div>
        {settings?.password_updated_at && (
          <p className="text-[10px] mt-3" style={{ color: 'var(--t-text-muted)' }}>
            Last changed: {new Date(settings.password_updated_at).toLocaleDateString()}
          </p>
        )}
      </div>

      {/* 2FA */}
      <div className="nx-card p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Smartphone className="w-4 h-4" style={{ color: '#0D9488' }} />
            <div>
              <h3 className="text-sm font-bold" style={{ color: 'var(--t-text-heading)' }}>Two-Factor Authentication</h3>
              <p className="text-[10px]" style={{ color: 'var(--t-text-muted)' }}>
                Add an extra layer of security to your account
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[10px] font-semibold" style={{ color: settings?.two_factor_enabled ? '#16A34A' : 'var(--t-text-muted)' }}>
              {settings?.two_factor_enabled ? 'Enabled' : 'Disabled'}
            </span>
            <Switch checked={settings?.two_factor_enabled || false} onCheckedChange={toggle2FA}
              disabled={toggling2FA} data-testid="2fa-toggle" />
          </div>
        </div>
        {backupCodes && (
          <div className="mt-4 p-3 rounded-lg" style={{ background: 'rgba(245,132,31,0.08)', border: '1px solid rgba(245,132,31,0.2)' }}>
            <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: '#F5841F' }}>
              Backup Codes: Save these securely
            </p>
            <div className="grid grid-cols-3 gap-1.5">
              {backupCodes.map((code, i) => (
                <code key={i} className="text-xs font-mono px-2 py-1 rounded text-center"
                  style={{ background: 'var(--t-bg-section)', color: 'var(--t-text-heading)' }}>
                  {code}
                </code>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Login Activity */}
      <div className="nx-card p-5">
        <div className="flex items-center gap-2 mb-4">
          <Globe className="w-4 h-4" style={{ color: '#0D9488' }} />
          <h3 className="text-sm font-bold" style={{ color: 'var(--t-text-heading)' }}>Account Activity</h3>
        </div>
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="p-3 rounded-lg" style={{ background: 'var(--t-bg-section)' }}>
            <p className="text-[10px] uppercase font-bold" style={{ color: 'var(--t-text-muted)' }}>Total Logins</p>
            <p className="text-lg font-black" style={{ color: 'var(--t-text-heading)' }}>{settings?.login_count || 0}</p>
          </div>
          <div className="p-3 rounded-lg" style={{ background: 'var(--t-bg-section)' }}>
            <p className="text-[10px] uppercase font-bold" style={{ color: 'var(--t-text-muted)' }}>Active Sessions</p>
            <p className="text-lg font-black" style={{ color: 'var(--t-text-heading)' }}>{settings?.active_sessions || 1}</p>
          </div>
          <div className="p-3 rounded-lg" style={{ background: 'var(--t-bg-section)' }}>
            <p className="text-[10px] uppercase font-bold" style={{ color: 'var(--t-text-muted)' }}>Last Login</p>
            <p className="text-sm font-bold mt-0.5" style={{ color: 'var(--t-text-heading)' }}>
              {settings?.last_login ? new Date(settings.last_login).toLocaleDateString() : 'Today'}
            </p>
          </div>
        </div>

        {/* Current Session */}
        <div className="border rounded-lg overflow-hidden" style={{ borderColor: 'var(--t-border-subtle)' }}>
          <div className="px-4 py-2.5" style={{ background: 'var(--t-bg-section)' }}>
            <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--t-text-muted)' }}>Active Sessions</p>
          </div>
          <div className="p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(13,148,136,0.1)' }}>
              <Globe className="w-4 h-4" style={{ color: '#0D9488' }} />
            </div>
            <div className="flex-1">
              <p className="text-xs font-bold" style={{ color: 'var(--t-text-heading)' }}>Current Browser</p>
              <p className="text-[10px]" style={{ color: 'var(--t-text-muted)' }}>This device &middot; Active now</p>
            </div>
            <span className="w-2 h-2 rounded-full" style={{ background: '#16A34A' }} />
          </div>
        </div>
      </div>
    </div>
  );
}

function PrivacyTab() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/settings/security').then(r => { setSettings(r.data?.privacy || {}); setLoading(false); }).catch(() => {
      setSettings({ public_profile: false, show_email: false, show_phone: false });
      setLoading(false);
    });
  }, []);

  const toggle = async (field, value) => {
    try {
      await api.put('/settings/privacy', { [field]: value });
      setSettings(prev => ({ ...prev, [field]: value }));
      toast.success('Privacy settings updated');
    } catch { toast.error('Failed to update'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" style={{ color: '#0D9488' }} /></div>;

  const items = [
    { key: 'public_profile', label: 'Public Profile', desc: 'Allow others to view your career profile', icon: User },
    { key: 'show_email', label: 'Show Email', desc: 'Display your email on your public profile', icon: Mail },
    { key: 'show_phone', label: 'Show Phone', desc: 'Display your phone number on your public profile', icon: Globe },
  ];

  return (
    <div className="space-y-3" data-testid="privacy-settings">
      {items.map(item => {
        const Icon = item.icon;
        return (
          <div key={item.key} className="nx-card p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Icon className="w-4 h-4" style={{ color: '#0D9488' }} />
              <div>
                <p className="text-sm font-semibold" style={{ color: 'var(--t-text-heading)' }}>{item.label}</p>
                <p className="text-[10px]" style={{ color: 'var(--t-text-muted)' }}>{item.desc}</p>
              </div>
            </div>
            <Switch checked={settings?.[item.key] || false} onCheckedChange={v => toggle(item.key, v)}
              data-testid={`privacy-${item.key}-toggle`} />
          </div>
        );
      })}
    </div>
  );
}

function NotificationsTab() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/settings/security').then(r => { setSettings(r.data?.notifications || {}); setLoading(false); }).catch(() => {
      setSettings({ email_notifications: true, assessment_reminders: true, weekly_digest: false, community_updates: false });
      setLoading(false);
    });
  }, []);

  const toggle = async (field, value) => {
    try {
      await api.put('/settings/notifications', { [field]: value });
      setSettings(prev => ({ ...prev, [field]: value }));
      toast.success('Notification preferences updated');
    } catch { toast.error('Failed to update'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" style={{ color: '#0D9488' }} /></div>;

  const items = [
    { key: 'email_notifications', label: 'Email Notifications', desc: 'Receive important updates via email' },
    { key: 'assessment_reminders', label: 'Assessment Reminders', desc: 'Get reminded to complete your assessments' },
    { key: 'weekly_digest', label: 'Weekly Digest', desc: 'Weekly summary of your career intelligence progress' },
    { key: 'community_updates', label: 'Community Updates', desc: 'Notifications about community activity' },
  ];

  return (
    <div className="space-y-3" data-testid="notification-settings">
      {items.map(item => (
        <div key={item.key} className="nx-card p-4 flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold" style={{ color: 'var(--t-text-heading)' }}>{item.label}</p>
            <p className="text-[10px]" style={{ color: 'var(--t-text-muted)' }}>{item.desc}</p>
          </div>
          <Switch checked={settings?.[item.key] || false} onCheckedChange={v => toggle(item.key, v)}
            data-testid={`notif-${item.key}-toggle`} />
        </div>
      ))}
    </div>
  );
}

function CalendarTab() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    calendarAPI.status().then(r => setStatus(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleConnect = async () => {
    try {
      const res = await calendarAPI.connect();
      if (res.data.authorization_url) {
        window.location.href = res.data.authorization_url;
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to connect';
      toast.error(msg);
    }
  };

  const handleDisconnect = async () => {
    try {
      await calendarAPI.disconnect();
      setStatus({ ...status, connected: false });
      toast.success('Google Calendar disconnected');
    } catch { toast.error('Failed to disconnect'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" style={{ color: '#0D9488' }} /></div>;

  return (
    <div className="space-y-4" data-testid="calendar-settings">
      <div className="nx-card p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(59,130,246,0.1)' }}>
            <Calendar className="w-5 h-5" style={{ color: '#3B82F6' }} />
          </div>
          <div>
            <h3 className="text-sm font-bold" style={{ color: 'var(--t-text-heading)' }}>Google Calendar</h3>
            <p className="text-[10px]" style={{ color: 'var(--t-text-muted)' }}>Sync career events and reminders</p>
          </div>
        </div>

        {!status?.configured ? (
          <div className="p-3 rounded-lg text-xs" style={{ background: 'rgba(245,132,31,0.1)', color: '#F5841F', border: '1px solid rgba(245,132,31,0.2)' }}>
            Google Calendar requires GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET to be configured in the backend.
          </div>
        ) : status?.connected ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg"
              style={{ background: 'rgba(22,163,74,0.1)', border: '1px solid rgba(22,163,74,0.2)' }}>
              <Check className="w-4 h-4" style={{ color: '#16A34A' }} />
              <span className="text-xs font-semibold" style={{ color: '#16A34A' }}>Connected to Google Calendar</span>
            </div>
            <Button onClick={handleDisconnect} className="h-9 text-xs font-bold"
              style={{ background: 'rgba(239,68,68,0.1)', color: '#EF4444', border: '1px solid rgba(239,68,68,0.2)' }}
              data-testid="disconnect-calendar-btn">
              <Unlink className="w-3.5 h-3.5 mr-1.5" /> Disconnect
            </Button>
          </div>
        ) : (
          <Button onClick={handleConnect} className="h-10 font-bold text-xs"
            style={{ background: '#3B82F6', color: '#fff' }}
            data-testid="connect-calendar-btn">
            <Calendar className="w-4 h-4 mr-1.5" /> Connect Google Calendar
          </Button>
        )}
      </div>
    </div>
  );
}

function DataExportTab() {
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get('/settings/export-data');
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kairera-data-export-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Data exported successfully');
    } catch { toast.error('Failed to export data'); }
    finally { setExporting(false); }
  };

  return (
    <div className="space-y-4" data-testid="data-export-settings">
      <div className="nx-card p-5">
        <div className="flex items-center gap-2 mb-2">
          <Download className="w-4 h-4" style={{ color: '#0D9488' }} />
          <h3 className="text-sm font-bold" style={{ color: 'var(--t-text-heading)' }}>Export Your Data</h3>
        </div>
        <p className="text-xs mb-4" style={{ color: 'var(--t-text-muted)' }}>
          Download all your data including profile, skills, assessment results, and career sessions as a JSON file.
        </p>
        <Button onClick={handleExport} disabled={exporting}
          className="h-9 px-5 text-xs font-semibold rounded-lg gap-1.5" style={{ background: '#1B3461', color: '#fff' }}
          data-testid="export-data-btn">
          {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
          {exporting ? 'Exporting...' : 'Download My Data'}
        </Button>
      </div>
    </div>
  );
}

function DangerZoneTab() {
  const [showDelete, setShowDelete] = useState(false);
  const [deleteForm, setDeleteForm] = useState({ password: '', confirmation: '' });
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (deleteForm.confirmation !== 'DELETE') { toast.error('Type DELETE to confirm'); return; }
    setDeleting(true);
    try {
      await api.post('/settings/delete-account', deleteForm);
      toast.success('Account scheduled for deletion');
      localStorage.clear();
      window.location.href = '/';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete account');
    } finally { setDeleting(false); }
  };

  return (
    <div className="space-y-4" data-testid="danger-zone-settings">
      <div className="rounded-xl p-5" style={{ border: '1px solid rgba(220,38,38,0.2)', background: 'rgba(220,38,38,0.03)' }}>
        <div className="flex items-center gap-2 mb-2">
          <Trash2 className="w-4 h-4" style={{ color: '#DC2626' }} />
          <h3 className="text-sm font-bold" style={{ color: '#DC2626' }}>Delete Account</h3>
        </div>
        <p className="text-xs mb-4" style={{ color: 'var(--t-text-muted)' }}>
          This will permanently delete your account and all associated data. This action cannot be undone.
        </p>
        {!showDelete ? (
          <Button onClick={() => setShowDelete(true)} variant="outline"
            className="h-9 px-5 text-xs font-semibold rounded-lg" style={{ borderColor: '#DC2626', color: '#DC2626' }}
            data-testid="show-delete-btn">
            I want to delete my account
          </Button>
        ) : (
          <div className="space-y-3 max-w-sm">
            <Input type="password" placeholder="Enter your password"
              value={deleteForm.password} onChange={e => setDeleteForm(p => ({ ...p, password: e.target.value }))}
              className="h-10" data-testid="delete-password-input" />
            <Input type="text" placeholder='Type "DELETE" to confirm'
              value={deleteForm.confirmation} onChange={e => setDeleteForm(p => ({ ...p, confirmation: e.target.value }))}
              className="h-10" data-testid="delete-confirmation-input" />
            <div className="flex gap-2">
              <Button onClick={() => setShowDelete(false)} variant="outline"
                className="h-9 px-4 text-xs rounded-lg">Cancel</Button>
              <Button onClick={handleDelete} disabled={deleting}
                className="h-9 px-4 text-xs font-semibold rounded-lg" style={{ background: '#DC2626', color: '#fff' }}
                data-testid="confirm-delete-btn">
                {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Trash2 className="w-3.5 h-3.5 mr-1.5" />}
                Delete Permanently
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState('security');

  return (
    <div data-testid="settings-page">
      <div className="mb-8">
        <p className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: '#0D9488' }}>Platform</p>
        <h1 className="text-2xl font-extrabold" style={{ color: 'var(--t-text-heading)' }}>Settings</h1>
        <p className="text-xs mt-1" style={{ color: 'var(--t-text-muted)' }}>Manage your account security, preferences, and integrations</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Enterprise Sidebar Nav */}
        <div className="lg:w-60 flex-shrink-0">
          <nav className="nx-card p-2 space-y-0.5" data-testid="settings-nav">
            {TABS.map(tab => {
              const Icon = tab.icon;
              const isActive = tab.id === activeTab;
              const isDanger = tab.id === 'danger';
              return (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`settings-tab-${tab.id}`}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left transition-all"
                  style={{
                    background: isActive ? (isDanger ? 'rgba(220,38,38,0.06)' : 'rgba(13,148,136,0.08)') : 'transparent',
                    borderLeft: isActive ? `3px solid ${isDanger ? '#DC2626' : '#0D9488'}` : '3px solid transparent',
                  }}>
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: isActive ? (isDanger ? 'rgba(220,38,38,0.1)' : 'rgba(13,148,136,0.1)') : 'var(--t-bg-section)' }}>
                    <Icon className="w-4 h-4" style={{ color: isActive ? (isDanger ? '#DC2626' : '#0D9488') : 'var(--t-text-muted)' }} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-bold truncate" style={{ color: isActive ? (isDanger ? '#DC2626' : '#0D9488') : 'var(--t-text-heading)' }}>{tab.label}</p>
                    <p className="text-[10px] truncate" style={{ color: 'var(--t-text-muted)' }}>{tab.desc}</p>
                  </div>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="flex-1 min-w-0">
          {activeTab === 'security' && <SecurityTab />}
          {activeTab === 'privacy' && <PrivacyTab />}
          {activeTab === 'notifications' && <NotificationsTab />}
          {activeTab === 'calendar' && <CalendarTab />}
          {activeTab === 'data' && <DataExportTab />}
          {activeTab === 'danger' && <DangerZoneTab />}
        </div>
      </div>
    </div>
  );
}
